/**
 * Jun-2026 (User-Wunsch) — zentrale Quelle für vom Hauptsitz gesperrte
 * DealerProfile-Felder, -Bereiche UND -Teilbereiche einer Filiale. Liefert
 * `locked_fields`, `locked_areas`, `locked_subgroups` von
 * GET /users/me/dealer-profile (modulweiter Cache) + Hilfsfunktionen, um
 * Inputs/Abschnitte/Teilbereiche auszugrauen & zu sperren.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";

let _cache = null;   // { fields: [], areas: [], subgroups: [] }
let _promise = null;
export function invalidateLockedFields() { _cache = null; _promise = null; }

// Jul-2026 (Bugfix, User-Meldung): Cache bei Konto-/Standortwechsel leeren —
// sonst zeigt der Hauptsitz fälschlich die Filial-Sperren einer früheren Sitzung.
if (typeof window !== "undefined") {
    window.addEventListener("tw-branch-changed", invalidateLockedFields);
    window.addEventListener("tw-auth-changed", invalidateLockedFields);
}

function _load() {
    if (_cache) return Promise.resolve(_cache);
    if (!_promise) {
        _promise = api.get("/users/me/dealer-profile")
            .then((r) => {
                _cache = {
                    fields: r.data?.locked_fields || [],
                    areas: r.data?.locked_areas || [],
                    subgroups: r.data?.locked_subgroups || [],
                };
                return _cache;
            })
            .catch(() => { _promise = null; return { fields: [], areas: [], subgroups: [] }; });
    }
    return _promise;
}

export function useLockedFields() {
    const [data, setData] = useState(_cache || { fields: [], areas: [], subgroups: [] });
    useEffect(() => {
        let alive = true;
        _load().then((d) => { if (alive) setData(d || { fields: [], areas: [], subgroups: [] }); });
        return () => { alive = false; };
    }, []);
    const set = new Set(data.fields || []);
    const areaSet = new Set(data.areas || []);
    const subSet = new Set(data.subgroups || []);
    return {
        lockedFields: set,
        lockedAreas: areaSet,
        lockedSubgroups: subSet,
        anyLocked: set.size > 0,
        isLocked: (f) => set.has(f),
        isAreaLocked: (a) => areaSet.has(a),
        isSubgroupLocked: (area, subKey) => subSet.has(`${area}.${subKey}`),
        // Props zum Sperren+Ausgrauen eines Inputs (mergt eine bestehende className).
        lockProps: (f, base = "") => set.has(f)
            ? { disabled: true, className: `${base} bg-gray-100 text-gray-400 cursor-not-allowed` }
            : { className: base },
    };
}
